import React, { useMemo, useState } from "react";
import { FaBars, FaSignOutAlt, FaTimes, FaArrowLeft } from "react-icons/fa";
import { Drawer, IconButton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { groupMenuItems } from "../SIdeMenu/menuItems";
import { logout, getSessionRole } from "../../utils/auth";
import {
  getNavMenuItems,
  normalizeNavPath,
  resolveNavTitle,
} from "../../utils/navTitle";
import RefreshPageButton from "../Buttons/RefreshPageButton";

const normalizePath = normalizeNavPath;

const isLinkActive = (pathname, link) => {
  if (!link) return false;
  const current = normalizePath(pathname);
  const target = normalizePath(link);
  if (current === target) return true;
  if (target !== "/admin" && target !== "/branch" && target !== "/loan-account") {
    return current.startsWith(`${target}/`);
  }
  return false;
};

const getMobileBackPath = (pathname) => {
  const path = normalizePath(pathname);
  const basePaths = ["/admin", "/branch", "/loan-account"];
  if (basePaths.includes(path)) return null;

  const segments = path.split("/").filter(Boolean);
  if (segments.length <= 2) {
    return `/${segments[0] || "admin"}`;
  }
  return `/${segments.slice(0, -1).join("/")}`;
};

const DrawerNavButton = ({ item, pathname, onNavigate }) => {
  const active = isLinkActive(pathname, item.link);
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.link)}
      className={`w-full flex items-center gap-3 mx-2 px-3 py-2.5 text-left text-sm rounded-xl transition-colors ${
        active
          ? "bg-white/15 text-white font-semibold ring-1 ring-white/15"
          : "text-white/85 hover:bg-white/10"
      }`}
    >
      <span
        className={`shrink-0 flex h-8 w-8 items-center justify-center rounded-lg ${
          active ? "bg-accent/25 text-[#f3df9f]" : "bg-white/10 opacity-90"
        }`}
      >
        {item.icon}
      </span>
      <span className="truncate">{item.title}</span>
    </button>
  );
};

const Navbar = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const roleNum = getSessionRole();
  const isSuperAdmin = roleNum === 0;

  const sideMenuItems = useMemo(() => {
    const items = getNavMenuItems();
    return Array.isArray(items) ? items : [];
  }, []);

  const groupedItems = useMemo(
    () => (isSuperAdmin ? groupMenuItems(sideMenuItems) : []),
    [isSuperAdmin, sideMenuItems]
  );

  const pathname = location.pathname;
  const title = resolveNavTitle(pathname, sideMenuItems);
  const backPath = getMobileBackPath(pathname);
  const showBack = Boolean(backPath);

  const toggleDrawer = (open) => () => setIsDrawerOpen(open);

  const handleLogout = () => logout();

  const handleNavigation = (link) => {
    navigate(link);
    setIsDrawerOpen(false);
  };

  const handleBack = () => {
    if (backPath) navigate(backPath);
    else navigate(-1);
  };

  return (
    <>
      <header className="hidden max850:flex fixed top-0 left-0 right-0 z-[1200] h-14 items-center gap-2 px-2.5 bg-main text-white shadow-lifted border-b border-white/10 backdrop-blur-sm">
        {showBack ? (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Go back"
            className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 active:bg-white/15 transition-colors"
          >
            <FaArrowLeft className="text-lg" />
          </button>
        ) : (
          <button
            type="button"
            onClick={toggleDrawer(true)}
            aria-label="Open menu"
            className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 active:bg-white/15 transition-colors"
          >
            <FaBars className="text-lg" />
          </button>
        )}

        <div className="flex-1 min-w-0 text-center px-1">
          <p className="text-sm font-bold truncate leading-tight">{title}</p>
          {isSuperAdmin && (
            <p className="text-[9px] text-white/55 uppercase tracking-wide truncate">
              Super Admin
            </p>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-0.5">
          <RefreshPageButton compact />
          {showBack ? (
            <button
              type="button"
              onClick={toggleDrawer(true)}
              aria-label="Open menu"
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 active:bg-white/15 transition-colors"
            >
              <FaBars className="text-lg" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Logout"
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 active:bg-white/15 transition-colors"
            >
              <FaSignOutAlt className="text-lg" />
            </button>
          )}
        </div>
      </header>

      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: 300,
            background:
              "linear-gradient(180deg, #155f60 0%, #0f4849 60%, #0a3d3e 100%)",
            color: "white",
          },
        }}
      >
        <div className="flex flex-col h-full font-quicksand">
          <div className="flex items-center justify-between gap-3 px-4 py-3.5 border-b border-white/10 bg-black/10">
            <div className="flex items-center gap-2.5 min-w-0">
              <img src="/logo.svg" alt="" className="h-8 w-auto shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-bold truncate">Irshad Carton</p>
                <p className="text-[10px] text-white/60 uppercase tracking-wider">
                  {isSuperAdmin ? "Super Admin" : "Navigation"}
                </p>
              </div>
            </div>
            <IconButton
              onClick={toggleDrawer(false)}
              className="!text-white hover:!bg-white/10"
              size="small"
            >
              <FaTimes className="text-base" />
            </IconButton>
          </div>

          <nav className="flex-1 overflow-y-auto py-2">
            {isSuperAdmin
              ? groupedItems.map((group) => (
                  <div key={group.label} className="mb-1">
                    <p className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white/45">
                      {group.label}
                    </p>
                    {group.items.map((item) => (
                      <DrawerNavButton
                        key={item.link}
                        item={item}
                        pathname={pathname}
                        onNavigate={handleNavigation}
                      />
                    ))}
                  </div>
                ))
              : sideMenuItems.map((item) => (
                  <DrawerNavButton
                    key={item.link}
                    item={item}
                    pathname={pathname}
                    onNavigate={handleNavigation}
                  />
                ))}
          </nav>

          <div className="p-3 border-t border-white/10 bg-black/10">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 hover:bg-red-500/20 hover:border-red-300/40 px-3 py-2.5 text-sm font-semibold transition-colors"
            >
              <FaSignOutAlt />
              Logout
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default Navbar;
